/*
 * AdminPurchaseHistoryPanel — Purchase History card for /admin/users/:userId.
 *
 * Shows:
 *   • Current subscription info (tier / billing cycle / status).
 *   • Charge Override toggle — when ON, subscription renewals log a $0 charge
 *     in the ledger but do NOT actually bill the user via Stripe (handled by
 *     the webhook path).
 *   • Coin Adjustment (SYSTEM_EDIT) — admin-only ± coin adjustment with
 *     required reason, writes to coin_ledger with full audit metadata.
 *   • Topups purchased.
 *   • Full payment ledger.
 */
import React, { useCallback, useEffect, useState } from "react";
import { authAxios } from "../../utils/auth";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";
import { Switch } from "../ui/switch";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { CreditCard, Crown, DollarSign, Loader2, ShoppingBag, Coins, Plus, Minus } from "lucide-react";

const fmtUSD = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;
const fmtDate = (v) => (v ? new Date(v).toLocaleString() : "—");
const tierColor = (t) => ({
  BRONZE: "text-amber-500", SILVER: "text-slate-300", GOLD: "text-yellow-400",
  PLATINUM: "text-white", DIAMOND: "text-white", FREE: "text-gray-500",
}[t] || "text-gray-400");

const StatusPill = ({ status }) => {
  const s = (status || "").toLowerCase();
  const cls = s === "paid" || s === "completed" || s === "active"
    ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
    : s === "pending" || s === "initiated" || s === "trialing"
    ? "bg-amber-500/15 text-amber-300 border border-amber-500/30"
    : "bg-white/5 text-white/50 border border-white/10";
  return (
    <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded ${cls}`}>
      {status || "—"}
    </span>
  );
};

export default function AdminPurchaseHistoryPanel({ userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingOverride, setSavingOverride] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [adjustSign, setAdjustSign] = useState("+"); // "+" credit / "-" debit
  const [adjustClassify, setAdjustClassify] = useState("bonus"); // "bonus" | "purchased"
  const [adjusting, setAdjusting] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await authAxios.get(`/admin/users/${userId}/purchase-history`);
      setData(r.data);
    } catch (err) {
      toast.error("Failed to load purchase history");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const toggleChargeOverride = async (next) => {
    setSavingOverride(true);
    try {
      await authAxios.put(`/admin/users/${userId}`, { charge_override: next });
      setData((d) => d ? { ...d, subscription: { ...d.subscription, charge_override: next } } : d);
      toast.success(next ? "Charge override enabled — renewals will log $0" : "Charge override disabled");
    } catch (err) {
      toast.error("Failed to update charge override");
    } finally {
      setSavingOverride(false);
    }
  };

  const submitAdjustment = async () => {
    const magnitude = parseInt(adjustAmount, 10);
    if (!magnitude || magnitude <= 0) {
      toast.error("Enter a positive coin amount");
      return;
    }
    if (!adjustReason.trim()) {
      toast.error("Reason is required");
      return;
    }
    const signed = adjustSign === "-" ? -magnitude : magnitude;
    if (!window.confirm(
      `Apply ${signed >= 0 ? "+" : ""}${signed.toLocaleString()} coins to this user?\n\nReason: ${adjustReason.trim()}`
    )) return;

    setAdjusting(true);
    try {
      const r = await authAxios.post(`/admin/users/${userId}/coin-adjust`, {
        amount: signed,
        reason: adjustReason.trim(),
        classify: adjustClassify,
      });
      const balanceAfter = Number(r?.data?.balance_after) || 0;
      // Optimistic prepend so the row appears immediately without waiting on
      // the /purchase-history refetch. `load()` below reconciles from the DB.
      setData((d) => d ? {
        ...d,
        admin_edits: [
          {
            id: `optimistic_${Date.now()}`,
            created_at: new Date().toISOString(),
            amount: signed,
            balance_after: balanceAfter,
            reason: adjustReason.trim(),
            admin_name: "you",
            admin_email: null,
            admin_user_id: null,
          },
          ...(d.admin_edits || []),
        ],
      } : d);
      toast.success(
        `Applied ${signed >= 0 ? "+" : ""}${signed.toLocaleString()} coins (${adjustClassify}) · new balance: ${balanceAfter.toLocaleString()}`
      );
      setAdjustAmount("");
      setAdjustReason("");
      setAdjustSign("+");
      setAdjustClassify("bonus");
      // Reconcile from the server (replaces the optimistic row with the real
      // admin identity + reference_id from the DB).
      await load();
    } catch (err) {
      console.error("[coin-adjust] failed:", err);
      toast.error(err?.response?.data?.detail || "Failed to adjust coins");
    } finally {
      setAdjusting(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-divine-charcoal border-divine-gold/20" data-testid="purchase-history-card">
        <CardContent className="py-6 flex items-center justify-center text-white/40 text-sm gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading purchase history…
        </CardContent>
      </Card>
    );
  }

  const sub = data?.subscription || {};
  const totals = data?.totals || {};
  const topups = data?.topups || [];
  const ledger = data?.ledger || [];
  const adminEdits = data?.admin_edits || [];

  return (
    <Card className="bg-divine-charcoal border-divine-gold/20" data-testid="purchase-history-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-divine-gold font-cinzel text-lg flex items-center gap-2">
          <CreditCard className="h-5 w-5" /> Purchase History
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Subscription info */}
        <div className="rounded-lg border border-divine-gold/10 bg-divine-black/50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Crown className={`h-5 w-5 ${tierColor(sub.tier)}`} />
              <div>
                <div className={`text-lg font-semibold ${tierColor(sub.tier)}`}>{sub.tier || "FREE"}</div>
                <div className="text-xs text-white/40">
                  {sub.billing_cycle ? `${sub.billing_cycle.charAt(0).toUpperCase() + sub.billing_cycle.slice(1)} billing` : "No active subscription"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <StatusPill status={sub.status || (sub.tier && sub.tier !== "FREE" ? "active" : "—")} />
            </div>
          </div>

          {/* Charge Override */}
          <div className="mt-4 pt-4 border-t border-white/[0.06] flex items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="text-sm text-white/80 font-medium">Payment charge override</div>
              <div className="text-xs text-white/40 mt-0.5">
                When ON, subscription renewals log a $0 charge in the ledger and don&apos;t bill via Stripe.
              </div>
            </div>
            <Switch
              checked={!!sub.charge_override}
              onCheckedChange={toggleChargeOverride}
              disabled={savingOverride}
              data-testid="charge-override-toggle"
            />
          </div>

          {sub.stripe_subscription_id && (
            <div className="mt-3 text-[11px] text-white/30 font-mono truncate">
              sub_id: {sub.stripe_subscription_id}
            </div>
          )}
        </div>

        {/* Topups summary */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-divine-gold/10 bg-divine-black/50 p-3">
            <div className="text-[10px] text-white/40 uppercase tracking-wide">Topups Paid</div>
            <div className="text-lg font-semibold text-white mt-1">{fmtUSD(totals.topups_paid_cents)}</div>
          </div>
          <div className="rounded-lg border border-divine-gold/10 bg-divine-black/50 p-3">
            <div className="text-[10px] text-white/40 uppercase tracking-wide">All-Time Paid</div>
            <div className="text-lg font-semibold text-emerald-300 mt-1">{fmtUSD(totals.all_paid_cents)}</div>
          </div>
        </div>

        {/* Coin Adjustment (SYSTEM_EDIT) — admin manual credit/debit with audit reason */}
        <div className="rounded-lg border border-amber-500/20 bg-amber-500/[0.03] p-4">
          <div className="flex items-center gap-2 mb-3">
            <Coins className="w-4 h-4 text-amber-400" />
            <div className="text-sm text-white/80 font-medium">Coin Adjustment</div>
            <span className="text-[10px] uppercase tracking-wide text-amber-400/70 border border-amber-500/30 px-1.5 py-0.5 rounded">SYSTEM_EDIT</span>
          </div>
          <div className="text-xs text-white/40 mb-3">
            Manually credit or debit coins. Logs to coin_ledger with your admin identity and reason. Debits cannot take a user negative.
          </div>
          <div className="flex flex-wrap items-stretch gap-2">
            {/* +/- toggle */}
            <div className="flex rounded-md border border-white/10 overflow-hidden shrink-0">
              <button
                type="button"
                onClick={() => setAdjustSign("+")}
                disabled={adjusting}
                data-testid="coin-adjust-sign-plus"
                className={`px-3 flex items-center gap-1 text-sm font-medium ${adjustSign === "+" ? "bg-emerald-500/20 text-emerald-300" : "text-white/40 hover:text-white/70"}`}
              >
                <Plus className="w-3.5 h-3.5" /> Credit
              </button>
              <button
                type="button"
                onClick={() => setAdjustSign("-")}
                disabled={adjusting}
                data-testid="coin-adjust-sign-minus"
                className={`px-3 flex items-center gap-1 text-sm font-medium border-l border-white/10 ${adjustSign === "-" ? "bg-rose-500/20 text-rose-300" : "text-white/40 hover:text-white/70"}`}
              >
                <Minus className="w-3.5 h-3.5" /> Debit
              </button>
            </div>
            <Input
              type="number"
              min="1"
              placeholder="Coins"
              value={adjustAmount}
              onChange={(e) => setAdjustAmount(e.target.value)}
              disabled={adjusting}
              data-testid="coin-adjust-amount"
              className="w-32 !bg-divine-black/70 !border-divine-gold/30 !text-white placeholder:!text-white/40"
            />
            <Input
              type="text"
              placeholder="Reason (required, appears in ledger)"
              value={adjustReason}
              onChange={(e) => setAdjustReason(e.target.value)}
              disabled={adjusting}
              data-testid="coin-adjust-reason"
              className="flex-1 min-w-[200px] !bg-divine-black/70 !border-divine-gold/30 !text-white placeholder:!text-white/40"
              maxLength={500}
            />
            {/* Classify toggle — Purchased grants transferable coins
                (moves to Affiliate Code Balance), Bonus stays non-transferable */}
            <div className="flex rounded-md border border-white/10 overflow-hidden shrink-0">
              <button
                type="button"
                onClick={() => setAdjustClassify("bonus")}
                disabled={adjusting}
                data-testid="coin-adjust-classify-bonus"
                title="Non-transferable — spendable on LLM only"
                className={`px-3 flex items-center gap-1 text-xs font-medium ${adjustClassify === "bonus" ? "bg-amber-500/20 text-amber-300" : "text-white/40 hover:text-white/70"}`}
              >
                Bonus
              </button>
              <button
                type="button"
                onClick={() => setAdjustClassify("purchased")}
                disabled={adjusting}
                data-testid="coin-adjust-classify-purchased"
                title="Transferable to Affiliate Code Balance"
                className={`px-3 flex items-center gap-1 text-xs font-medium border-l border-white/10 ${adjustClassify === "purchased" ? "bg-emerald-500/20 text-emerald-300" : "text-white/40 hover:text-white/70"}`}
              >
                Purchased
              </button>
            </div>
            <Button
              onClick={submitAdjustment}
              disabled={adjusting || !adjustAmount || !adjustReason.trim()}
              data-testid="coin-adjust-submit"
              className={adjustSign === "-" ? "bg-rose-600 hover:bg-rose-500 text-white" : "bg-emerald-600 hover:bg-emerald-500 text-white"}
            >
              {adjusting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
            </Button>
          </div>
        </div>

        {/* Topups table */}
        {topups.length > 0 && (
          <div>
            <div className="text-xs text-white/50 mb-2 flex items-center gap-1.5">
              <ShoppingBag className="w-3.5 h-3.5" /> Topups ({topups.length})
            </div>
            <div className="rounded-md border border-white/[0.06] overflow-hidden max-h-64 overflow-y-auto">
              <table className="w-full text-xs" data-testid="topups-table">
                <thead className="bg-white/[0.03] text-white/50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Date</th>
                    <th className="text-right px-3 py-2 font-medium">Amount</th>
                    <th className="text-right px-3 py-2 font-medium">Coins</th>
                    <th className="text-right px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="text-white/80">
                  {topups.map((t) => (
                    <tr key={t.session_id} className="border-t border-white/[0.04]">
                      <td className="px-3 py-1.5 text-white/60">{fmtDate(t.created_at)}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{fmtUSD(t.amount_cents)}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-amber-400">
                        {((t.base_coins || 0) + (t.bonus_coins || 0)).toLocaleString()}
                      </td>
                      <td className="px-3 py-1.5 text-right"><StatusPill status={t.payment_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Full payment ledger */}
        {ledger.length > 0 && (
          <div>
            <div className="text-xs text-white/50 mb-2 flex items-center gap-1.5">
              <DollarSign className="w-3.5 h-3.5" /> Payment Ledger ({ledger.length})
            </div>
            <div className="rounded-md border border-white/[0.06] overflow-hidden max-h-72 overflow-y-auto">
              <table className="w-full text-xs" data-testid="payment-ledger-table">
                <thead className="bg-white/[0.03] text-white/50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Date</th>
                    <th className="text-left px-3 py-2 font-medium">Type</th>
                    <th className="text-right px-3 py-2 font-medium">Amount</th>
                    <th className="text-right px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="text-white/80">
                  {ledger.map((r) => (
                    <tr key={r.session_id} className="border-t border-white/[0.04]">
                      <td className="px-3 py-1.5 text-white/60">{fmtDate(r.created_at)}</td>
                      <td className="px-3 py-1.5">
                        <span className="text-white/70">{r.purchase_type}</span>
                        {r.subscription_tier && (
                          <span className={`ml-2 text-[10px] ${tierColor(r.subscription_tier)}`}>{r.subscription_tier}</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono">{fmtUSD(r.amount_cents)}</td>
                      <td className="px-3 py-1.5 text-right"><StatusPill status={r.payment_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {topups.length === 0 && ledger.length === 0 && adminEdits.length === 0 && (
          <div className="text-center text-white/40 text-sm py-6">No purchases yet.</div>
        )}

        {/* Admin Edits (SYSTEM_EDIT rows from coin_ledger) — always rendered
            in edit mode so admins can see the section exists even when empty. */}
        <div>
          <div className="text-xs text-white/50 mb-2 flex items-center gap-1.5">
            <Coins className="w-3.5 h-3.5 text-amber-400" /> Admin Edits ({adminEdits.length})
          </div>
          {adminEdits.length === 0 ? (
            <div className="text-center text-white/30 text-xs py-3 border border-dashed border-white/[0.08] rounded-md">
              No admin edits recorded for this user yet.
            </div>
          ) : (
            <div className="rounded-md border border-amber-500/20 overflow-hidden max-h-64 overflow-y-auto">
              <table className="w-full text-xs" data-testid="admin-edits-table">
                <thead className="bg-amber-500/[0.05] text-white/50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Date</th>
                    <th className="text-left px-3 py-2 font-medium">Admin</th>
                    <th className="text-left px-3 py-2 font-medium">Reason</th>
                    <th className="text-right px-3 py-2 font-medium">Coins</th>
                    <th className="text-right px-3 py-2 font-medium">Balance After</th>
                  </tr>
                </thead>
                <tbody className="text-white/80">
                  {adminEdits.map((e) => {
                    const amt = Number(e.amount) || 0;
                    const isCredit = amt >= 0;
                    return (
                      <tr key={e.id} className="border-t border-white/[0.04]">
                        <td className="px-3 py-1.5 text-white/60 whitespace-nowrap">{fmtDate(e.created_at)}</td>
                        <td className="px-3 py-1.5 text-white/70 whitespace-nowrap" title={e.admin_email || e.admin_user_id || ""}>
                          {e.admin_name || e.admin_email || "—"}
                        </td>
                        <td className="px-3 py-1.5 text-white/80" title={e.reason}>{e.reason || "—"}</td>
                        <td className={`px-3 py-1.5 text-right font-mono ${isCredit ? "text-emerald-300" : "text-rose-300"}`}>
                          {isCredit ? "+" : ""}{amt.toLocaleString()}
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono text-amber-400">
                          {(Number(e.balance_after) || 0).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
